import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarToday, ChevronRight, OpenInNew, ContentCopy } from '@mui/icons-material';
import { Badge, Button, IconButton, Tooltip } from '@mui/material';

import { AppHeader } from '../components/app-header';
import { FilterableDataTable } from '../components/filterable-data-table';
import { LoadingState } from '../components/loading-state';
import { useGetProviderAppointmentsQuery, useGetProviderInfoQuery } from '../store/api';
import { formatDateTime } from '../utils/formatting';

export default function AppointmentsListPage() {
  const { providerId: urlProviderId } = useParams<{ providerId: string }>();

  // Handle URL encoding issues
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // State for pagination and filtering
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [filters, setFilters] = useState<Record<string, string>>({});

  // Memoized filter change handler
  const handleFiltersChange = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  }, []);

  // Fetch provider info from RTK Query
  const { data: provider, isLoading: userLoading } = useGetProviderInfoQuery(
    { providerId: providerId || '' },
    { skip: !providerId },
  );

  const getStatusBadgeColor = (
    status: string | undefined | null,
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'active':
        return 'success';
      case 'pending':
      case 'processing':
        return 'warning';
      case 'failed':
      case 'error':
      case 'cancelled':
        return 'error';
      case 'draft':
      case 'inactive':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Get URL for each row
  const getRowUrl = useCallback((appointment: any) => {
    return `/admin-v2/appointment/${appointment.appointment_id}`;
  }, []);

  // Fetch provider's appointments with pagination and filtering
  const { data: appointmentsData, isLoading: appointmentsLoading } =
    useGetProviderAppointmentsQuery(
      {
        providerId: providerId ?? '',
        page,
        perPage,
        ...filters,
      },
      {
        skip: !providerId,
      },
    );

  if (!providerId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <p className="text-gray-600 mt-2">No provider ID specified.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading provider details..." />
        </main>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Provider not found</h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const appointmentsColumns = [
    {
      key: 'appointment_id',
      label: 'Appointment ID',
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-blue-600 break-all font-mono">{value}</span>
          <Tooltip title="Copy ID">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                void navigator.clipboard.writeText(value);
              }}
            >
              <ContentCopy fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'title',
      label: 'Title',
      render: (value: string) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'encounter_type',
      label: 'Type',
      render: (value: string) => <span className="text-sm text-gray-900">{value}</span>,
    },
    {
      key: 'patient_id',
      label: 'Patient ID',
      render: (value: string | null) => (
        <span className="text-sm text-gray-900">{value || 'N/A'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <Badge color={getStatusBadgeColor(value)}>{value.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'start_time',
      label: 'Scheduled',
      render: (value: string) => (
        <span className="text-sm text-gray-500">{formatDateTime(value)}</span>
      ),
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (value: number) => <span className="text-sm text-gray-500">{value} min</span>,
    },
  ];

  const filterConfigs = [
    {
      key: 'status',
      label: 'Status',
      type: 'select' as const,
      options: [
        { value: 'scheduled', label: 'Scheduled' },
        { value: 'completed', label: 'Completed' },
        { value: 'cancelled', label: 'Cancelled' },
        { value: 'no_show', label: 'No Show' },
      ],
    },
    {
      key: 'appointmentType',
      label: 'Appointment Type',
      type: 'text' as const,
    },
    {
      key: 'encounterId',
      label: 'Encounter ID',
      type: 'text' as const,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}>
                  <Button
                    variant="text"
                    className="text-sm font-medium text-gray-700 hover:text-blue-600"
                  >
                    {provider.name}
                  </Button>
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">Appointments</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Page Header */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <CalendarToday className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">Appointments</h1>
                  <p className="text-gray-600">All appointments for {provider.name}</p>
                  <p className="text-sm text-gray-500">Provider ID: {provider.provider_id}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Total Appointments</div>
                <div className="text-2xl font-bold text-gray-900">
                  {appointmentsData?.total || 0}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filterable Data Table */}
        <FilterableDataTable
          title="Provider Appointments"
          columns={appointmentsColumns as any}
          data={appointmentsData?.items || []}
          total={appointmentsData?.total}
          loading={appointmentsLoading}
          page={page}
          perPage={perPage}
          filters={filterConfigs}
          onPageChange={setPage}
          onPerPageChange={setPerPage}
          onFiltersChange={handleFiltersChange}
          getRowUrl={getRowUrl}
          emptyMessage="No appointments to display"
        />
      </main>
    </div>
  );
}
